import PropTypes from "prop-types"
import Icon from "components/planning_center_icon"
import { BlankState, Table } from "components/ui"
import classnames from "classnames"
import { css } from "@emotion/core"
import { formatDateShort } from "utils/dates"

const cellStyles = (row, name) => {
  const styles = {
    canceledCell: row.canceled
      ? css`
          text-decoration: line-through;
        `
      : "",
    statusCell: css`
      border-left-width: 6px;
      border-left-style: solid;
      &.status-incomplete {
        border-left-color: #faac59;
      }
      &.status-refund-due {
        border-left-color: #df5a4c;
      }
      &.status-complete {
        border-left-color: #69c36e;
      }
      &.status-canceled {
        border-left-color: #cccccc;
      }
    `
  }

  return styles[name]
}

const cellClassNames = (row, _column) => {
  const isAttendeeIncomplete = row.incomplete

  const isAttendeeComplete = row.complete

  return {
    className: `ws-nw ${classnames({
      "status-incomplete": isAttendeeIncomplete,
      "status-canceled": row.canceled && !row.refundDue,
      "status-refund-due": row.refundDue,
      "status-complete": isAttendeeComplete
    })}`,
    css: cellStyles(row, "statusCell")
  }
}

const stylesToMakeSureTooltipsDontOverflowTheTable = css`
  [data-tooltip]:before {
    max-width: 500px;
  }
  tr:last-child [data-tooltip-location="bottom"]:before,
  tr:nth-last-child(2):not(:first-child) [data-tooltip-location="bottom"]:before {
    transform: translate(-50%, -5px) scale(0.5);
    bottom: calc(100% + 5px);
    top: auto;
  }
  tr:last-child [data-tooltip-location="bottom"]:hover:before,
  tr:nth-last-child(2):not(:first-child) [data-tooltip-location="bottom"]:hover:before {
    transform: translate(-50%, -5px) scale(1);
  }
  tr:last-child [data-tooltip-location="bottom"]:after,
  tr:nth-last-child(2):not(:first-child) [data-tooltip-location="bottom"]:after {
    border-color: rgba(55, 64, 70, 0.9) transparent transparent transparent;
    border-width: 5px 5px 0 5px;
    bottom: calc(100% + 5px);
    top: auto;
    transform-origin: top;
    transform: translateX(-50%) scaleY(0);
  }
  tr:last-child [data-tooltip-location="bottom"]:hover:after,
  tr:nth-last-child(2):not(:first-child) [data-tooltip-location="bottom"]:hover:after {
    transform: translateX(-50%) scaleY(1);
  }
`

const renderBalanceStyles = css`
  &:hover {
    z-index: 3 !important;
  }
`

const RegistrationsTable = ({ event, attendees, isFetching, onSortClick }) => {
  const handleRowClick = (row, e) => {
    if (e.target.className !== "icon") {
      if (e.metaKey || e.shiftKey || e.altKey || e.ctrlKey) {
        window.open(row.paths.registrationShow, "_blank")
      } else {
        window.location.assign(`${row.paths.registrationShow}`)
      }
    }
  }

  const renderDash = () => <span>&mdash;</span>

  const renderSuccessStatus = (text = null) => (
    <div>
      <span className="c-success">
        <Icon symbol="general#check" style={{ verticalAlign: "middle" }} />
      </span>
      {text && ` ${text}`}
    </div>
  )

  const renderWarningStatus = (text = null) => (
    <div>
      <span className="c-warning font-size-1 lh-1">&#8226;</span>
      {text && ` ${text}`}
    </div>
  )

  const renderIncompleteStatus = (text = null) => (
    <div>
      <span className="c-danger font-size-1 lh-1">&#8226;</span>
      {text && ` ${text}`}
    </div>
  )

  const renderBalance = (row, _column) => {
    if (row.refundDue) {
      return renderIncompleteStatus("Refund due")
    }

    if (!row.canceled) {
      return (
        <>
          <div className="d-f">
            {row.balanceStatus == "complete" ? (
              renderSuccessStatus()
            ) : (
              <>
                {renderWarningStatus(`${row.balanceStatus}`)}
                {!row.singleAttendeeRegistration ? (
                  <span
                    className="ml-4p p-r t-2p c-neutral-500"
                    data-tooltip="This registration has multiple attendees. The balance due for each attendee is calculated as a percentage of the total."
                    data-tooltip-location="bottom"
                  >
                    <Icon symbol="general#outlined-info-circle" />
                  </span>
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
        </>
      )
    }
  }

  const renderAttendeeInfo = (row, _column) => {
    if (row.canceled) {
      return null
    }

    if (event.profileCollect === "attendee_name") {
      return renderDash()
    } else if (row.profileInfoStatus == "complete") {
      return renderSuccessStatus()
    } else if (row.profileInfoStatus == "incomplete") {
      return renderWarningStatus()
    } else {
      return renderDash()
    }
  }

  const renderInfoToCollectQuestions = (row, _column) => {
    if (row.canceled) {
      return null
    }

    if (row.questionsStatus == "complete") {
      return renderSuccessStatus()
    } else if (row.questionsStatus == "incomplete") {
      return renderWarningStatus()
    } else {
      return renderDash()
    }
  }

  const renderFormStatus = (row, _column) => {
    if (row.canceled) {
      return null
    }

    if (row.formsStatus == "incomplete") {
      return renderWarningStatus(row.formsCount)
    } else if (row.formsStatus == "complete") {
      return renderSuccessStatus(row.formsCount)
    } else {
      return renderDash()
    }
  }

  const renderCounts = (row, _column) => {
    return row.countWithAttendance || row.count
  }

  const columns = [
    {
      cell: "registrationId",
      label: "#",
      isShown: true,
      isSortable: false,
      renderProps: cellClassNames
    },
    {
      cell: "registeredAt",
      label: "Submitted",
      isShown: true,
      isSortable: true,
      render: (row, _col) => formatDateShort(row.submittedAt)
    },
    {
      cell: "contact",
      label: "Name",
      isShown: true,
      isSortable: true,
      render: (row, _col) => row.personName,
      renderProps: (row, _column) => ({
        className: "ws-nw",
        css: cellStyles(row, "canceledCell")
      })
    },
    {
      cell: "attendee.attendeeTypeName",
      label: "Selection",
      isShown: true,
      isSortable: true,
      render: (row, _col) => row.selectionName,
      renderProps: (row, _column) => ({
        css: cellStyles(row, "canceledCell")
      })
    },
    {
      cell: "date",
      label: "For Date",
      isShown: event.isRegisterForSeparateTimesEnabled,
      isSortable: false
    },
    {
      cell: "count",
      label: "Count",
      isShown: event.isSimple,
      isSortable: true,
      render: renderCounts
    },
    {
      cell: "balance",
      label: "Balance",
      isShown: event.hasCost,
      isSortable: false,
      render: renderBalance,
      renderProps: () => ({
        className: "ws-nw",
        css: renderBalanceStyles
      })
    },
    {
      cell: "questions",
      className: "ta-c",
      label: "Questions",
      isShown: true,
      isSortable: false,
      render: renderInfoToCollectQuestions
    },
    {
      cell: "personalInfo",
      className: "ta-c",
      label: "Profile info",
      isShown: !event.isSimple,
      isSortable: false,
      render: renderAttendeeInfo
    },
    {
      cell: "forms",
      className: "ta-c",
      label: "Forms",
      isShown: !event.isSimple,
      isSortable: false,
      render: renderFormStatus
    }
  ]

  if (attendees.length > 0 || isFetching) {
    return (
      <Table
        columns={columns.filter(c => c.isShown)}
        rows={attendees}
        isLoading={isFetching}
        onRowClick={handleRowClick}
        onSortClick={onSortClick}
        className="table registrations-table"
        css={stylesToMakeSureTooltipsDontOverflowTheTable}
        overflowWrapperStyle={{ minHeight: 170 }}
        defaultSort="registeredAt"
        defaultDir="desc"
      />
    )
  } else {
    return (
      <BlankState
        imageURL="/static/blank-states/no-filtered-results.svg"
        placeholderText="There are no registrations that match the current filter."
      />
    )
  }
}

RegistrationsTable.propTypes = {
  event: PropTypes.shape({}),
  attendees: PropTypes.array,
  isFetching: PropTypes.bool,
  onSortClick: PropTypes.func
}

export default RegistrationsTable
